import { Col, Row } from "~/common/components/Layout";
import { Link } from "~/common/components/Link";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { isSpecialPrice } from "~/features/productDetails/redux/productDetailsSelectors";
import { useTranslation } from "react-i18next";
import * as React from "react";
import { type MouseEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectIsCartAmountsIsLoading } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { type PackagingInformationEntry } from "~/domains/product/types/ProductData";
import {
  findMinOrderQuantity,
  getDefaultQuantity,
  shouldShowPriceReference,
  usePdpSummaryLogic,
} from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { getPriceInfo, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { buildPackagingOptions } from "~/domains/product/utils/buildPackagingOptions";
import { selectPriceScalingIndex } from "~/utils/pricePicker";
import { isOfferPriceValid } from "~/utils/packageEntryUtils";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { trackPdpWishlistClick } from "~/app/analytics/ga/ga";
import { openAddToWishlistWizard } from "~/features/wishlist/components/modals/AddToWishlist/AddToWishlistService";
import SalesmanDetailsToggle from "~/features/productDetails/components/AddToCartCard/SalesmanAddToCartCard/SalesmanDetailsToggle";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import PriceInput from "~/features/productDetails/components/AddToCartCard/SalesmanAddToCartCard/PriceInput";
import SimplePriceText, { PriceTextVariants } from "~/domains/pricing/components/SimplePriceText/SimplePriceText";
import {
  obtainSpecialPriceLabel,
  Price,
  PriceType,
  type ProductOtherPriceProps,
} from "~/domains/pricing/components/Price/v1/Price";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "~/utils/constants";
import { useAppConfig } from "~/app/config/useAppConfig";
import { canSeeListPrice } from "~/utils/features";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import SalesmanPriceTable from "~/features/productDetails/components/AddToCartCard/SalesmanAddToCartCard/SalesmanPriceTable";
import { PREFERRED_PRODUCTS_ID } from "~/features/productDetails/constants";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";

import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import CenterContents from "~/common/components/CenterContents";
import { Stack, styled, Card, Grid, Typography } from "~/common/mui";
import { PackagingSelection } from "~/domains/product/components/PackagingSelection/PackagingSelection";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import type { AddToCartFormDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { useFormContext } from "react-hook-form";
import {
  getPurchaseDataFromUrl,
  selectAmountInCart,
} from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import type { NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import type { SourceInfo } from "react-number-format";
import { Alert } from "~/common/mui/Alert/Alert";
import { InfoOrderHandlingUnitQuantity } from "~/features/productDetails/components/ProductSummary/InfoOrderHandlingUnitQuantity";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { renderButtons } from "~/features/productDetails/components/ProductSummary/components/Buttons/usePdpButtons";
import { TabId } from "~/features/productDetails/models/TabId";
import MMDAssistant from "~/features/productDetails/components/MMDAssistant/MMDAssistant";
import { useDisplayMMDAssistant } from "~/features/productDetails/context/DisplayMMDAssistantContext";
import { usePdpCmnLogic } from "~/features/productDetails/components/MMDAssistant/logic/usePdpCmnLogic";
import { Product } from "~/domains/product/Product";
import { isMinOrderQuantityRestrictionEnabled } from "~/features/cart/cart/utils/cartUtils";
import { usePackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { VariantSelect } from "./components/VariantSelect";
import { useVariantContext } from "../../context/VariantContext";
import { ProductDetailsSection } from "./components/ProductDetailsSection";
import { addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";

const SalesmanPackagingArea = styled("div")(({ theme }) => ({
  background: theme.palette.surface.main,
  display: "flex",
  flexFlow: "column",
  flex: 1,
  borderRadius: `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`,
  height: "100%",
}));

const SalesmanPackagingAreaContent = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  flex: 1,
}));

interface Props {
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  hasPreferredProducts: boolean;
  changeTab: (tabId: string) => void;
}

interface AddToCartCardState {
  offerPrice?: PriceData;
  targetPrice?: PriceData;

  showDetails: boolean;
  priceOverriden: boolean;

  sending: boolean;
}

/*
 * Product Details page summary (starting from breadcrumbs, finishing before tabs) for Salesmen
 */
export const SalesmanPdpSummary = ({ productDescriptor, hasPreferredProducts, changeTab }: Props) => {
  const currencyIso = useAppConfig()?.formatting.currencyIso ?? "";
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reco_place = usePurchasePlace();
  const displayMMDAssistant = useDisplayMMDAssistant();

  const formProps = useFormContext<AddToCartFormDraft>();
  const { main, initialPackCodeResolved } = useAddToCartDerived();

  const product = productDescriptor.base;
  const restrictedCode = usePackageRestrictionContext();

  const selectedPackageCode = formProps.watch("selectedPackCode");
  const onSelectPackage = (packCode: string) => formProps.setValue("selectedPackCode", packCode);
  const packagingOptions = React.useMemo(() => {
    const entries = productDescriptor.availability?.packagingInformation ?? [];
    const filteredEntries = restrictedCode ? entries.filter((entry) => entry.packCode === restrictedCode) : entries;
    return buildPackagingOptions(filteredEntries.length > 0 ? filteredEntries : entries, productDescriptor, {}, t);
  }, [productDescriptor, restrictedCode, t]);

  const { variantLoading } = useVariantContext();

  const quantity = formProps.watch("quantity");
  const priceReference = formProps.watch("priceReference");

  const [state, setState] = React.useState<AddToCartCardState>({
    showDetails: false,
    priceOverriden: false,
    sending: false,
  });

  const webServicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;

  const availabilityInfo = productDescriptor.availability;
  const isLoading = productDescriptor.isLoading;
  const isError = productDescriptor.isError && !webServicesDisabled;

  const productFamilyName = product.preferredProductsFamilyName;
  const isExternalProduct = product.externalProduct;
  const quantityVisible = product.quantityVisible && !(product.externalProduct && isProductPMA(productDescriptor));
  const { selection } = usePriceReferenceSelection();
  const isPriceRefVisible = shouldShowPriceReference(isExternalProduct, productDescriptor, selection);

  const { priceOverriden, showDetails } = state;
  const packagingInformation = availabilityInfo?.packagingInformation ?? [];
  const selectedPackage: PackagingInformationEntry | undefined = packagingInformation.find(
    (pack) => pack.packCode === selectedPackageCode,
  );
  const packSize = selectedPackage?.packSize ?? 1;
  const packCode = selectedPackage?.packCode ?? "";

  const qtyInCart = useSelector(selectAmountInCart({ product: productDescriptor, packCode }));
  const isCartLoading = useSelector(selectIsCartAmountsIsLoading);

  const minOrderQuantity = findMinOrderQuantity(productDescriptor, packCode);

  const {
    buttons: { showWishlistButton },
    flags: { isPackageSizeInvalid, MOQHelperLabel, showManufacturerLogo },
  } = usePdpSummaryLogic({
    product: productDescriptor,
    qtyInCart,
    quantity,
    packCode,
    packSize,
    // isOnSale: hasSales,
  });

  const priceInfo = getPriceInfo({ product: productDescriptor, packCode });

  const isAddToCartEnabled =
    isProductPMA(productDescriptor) ||
    (priceInfo && priceInfo.hasPrice && availabilityInfo && availabilityInfo.hasPackaging);

  const qtyInputEnabled = !isLoading && (!isError || showWishlistButton) && product.quantityVisible;

  const wsError = productDescriptor.isError;
  const addToCartDisabled = !isAddToCartEnabled || isPackageSizeInvalid || !selectedPackage || wsError;

  const nettoPrice = main.netPrice;
  const priceUnit = main.netPrice?.unit ?? 1;

  const offerPrice = !priceOverriden ? nettoPrice : state.offerPrice;

  const targetPrice = state.targetPrice;

  let isInvalid = false;

  if (showDetails && priceInfo) {
    const priceScalingIndex = selectPriceScalingIndex(quantity + qtyInCart, priceInfo.priceScale);
    let msp = undefined;
    if (priceInfo) {
      const scalingEntry = priceInfo.priceScaleMSP[priceScalingIndex];
      msp = scalingEntry ? scalingEntry.price : undefined;
    }

    if (msp && priceOverriden && state.offerPrice) {
      isInvalid = !isOfferPriceValid(state.offerPrice, msp);
    }
  }

  const userIsSupposedToSeePrice = userHasPermission(Permission.PRICE);

  const isSpecial = isSpecialPrice(productDescriptor, packCode);

  const toggleDetails = () => setState((prevState) => ({ ...prevState, showDetails: !prevState.showDetails }));
  const updateOfferPrice = (e: NumberFormatEvent, sourceInfo: SourceInfo) => {
    if (sourceInfo.source === "prop") {
      return;
    }
    return setState((prevState) => ({
      ...prevState,
      offerPrice: e
        ? {
            value: e.floatValue ?? 0,
            currencyIso,
          }
        : undefined,
      priceOverriden: true,
    }));
  };
  const blurOfferPrice = () => {
    setState((prevState) => ({ ...prevState, priceOverriden: prevState.offerPrice !== undefined }));
  };
  const updateTargetPrice = (e: NumberFormatEvent, sourceInfo: SourceInfo) => {
    if (sourceInfo.source === "prop") {
      return;
    }
    setState((prevState) => ({
      ...prevState,
      targetPrice: e
        ? {
            value: e.floatValue ?? 0,
            currencyIso,
          }
        : undefined,
    }));
  };
  const updateQuantity = (quantity: number) =>
    formProps.setValue("quantity", quantity, { shouldTouch: true, shouldDirty: true });

  const openModalWishlist = (e?: MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }
    trackPdpWishlistClick(product.designation, 1);

    openAddToWishlistWizard({
      productCode: product.code,
      initialQuantity: formProps.getValues("quantity"),
      initialPriceReference: getInitialPriceReference(selection),
      addTracking: (product) => trackPdpWishlistClick(product.base.designation, 2),
    });
  };

  const addToQuote = () => {
    const packageData = selectedPackage!;
    const { priceScale, priceScaleMSP, priceScaleRSP } = priceInfo!;

    const priceScalingIndex = selectPriceScalingIndex(quantity + qtyInCart, priceScale);
    const msp = priceScaleMSP[priceScalingIndex] ? priceScaleMSP[priceScalingIndex].price : undefined;
    const rsp = priceScaleRSP[priceScalingIndex] ? priceScaleRSP[priceScalingIndex].price : undefined;

    setState((prevState) => ({ ...prevState, sending: true }));

    const offerPrice = state.offerPrice;

    const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
    const context = {
      page: reco_place,
      recommendationPlace,
      recommendationType,
    };

    dispatch(
      addToCart({
        items: [
          {
            quantity: quantity,
            productCode: product.code,
            packageCode: packCode,
            packageSize: packageData.packSize,
            primaryWarehouseCode: packageData.primaryWarehouseCode,
            selectedDeliveryDate: new Date(),
            offerPrice: offerPrice ? offerPrice.value : undefined,
            targetPrice: state.targetPrice ? state.targetPrice.value : undefined,
            msp: msp ? msp.value : undefined,
            rsp: rsp ? rsp.value : undefined,
            priceReference,
          },
        ],
        page: context.page,
        recommendationPlace: context.recommendationPlace,
        recommendationType: context.recommendationType,
        successUi: "toast",
        errorUi: "toast",
      }),
    )
      .then(() => {
        setState((prevState) => ({ ...prevState, sending: false }));
      })
      .catch(() => {
        setState((prevState) => ({ ...prevState, sending: false }));
      });
  };

  const buttons = renderButtons(productDescriptor, {
    atc: {
      disabled: webServicesDisabled || addToCartDisabled || state.sending || variantLoading,
      onClick: addToQuote,
    },
    wishlist: {
      disabled: variantLoading,
      onClick: openModalWishlist,
    },
    configurator: {
      disabled: variantLoading,
      onClick: () => changeTab(TabId.CONFIGURATOR),
    },
    atcFromSP: {
      disabled: variantLoading,
      quantity,
    },
  });

  const mmdCmnData = usePdpCmnLogic();

  const isMOQCheckEnabled = isMinOrderQuantityRestrictionEnabled();

  let qtyHelperLabel =
    isMOQCheckEnabled && minOrderQuantity > 1 ? t("page.moq.helperText.moqNotMet", { minOrderQuantity }) : undefined;
  let qtyIsError = false;
  if (isPackageSizeInvalid) {
    qtyHelperLabel = isMOQCheckEnabled ? MOQHelperLabel : t("addToCartPopup.mismatchedQty");
    qtyIsError = isPackageSizeInvalid;
  }
  const minQtyValue = isMOQCheckEnabled ? getDefaultQuantity(packSize, minOrderQuantity) : packSize;

  const col1 = (
    <Stack gap={2}>
      <Box sx={{ position: "relative" }}>
        <Product.PrimaryImageWithLogosV2 logoWidth={66} gap={24} size={"1fr"} />
      </Box>

      <ProductDetailsSection />
    </Stack>
  );

  const col2 = (
    <SalesmanPackagingArea>
      <SalesmanPackagingAreaContent>
        {productDescriptor.isAvailabilityLoading && (
          <CenterContents className={"mb-30 w-100"}>
            <CircleSpinner />
          </CenterContents>
        )}
        {productDescriptor.availability && initialPackCodeResolved && (
          <PackagingSelection value={selectedPackageCode} onChange={onSelectPackage} options={packagingOptions} />
        )}
      </SalesmanPackagingAreaContent>
    </SalesmanPackagingArea>
  );

  const col3 = (
    <Box>
      <Stack gap={2}>
        {!wsError && quantityVisible && (
          <SalesmanDetailsToggle
            data-test-id={"pdp-toggle-details"}
            className={"flex-justify-end"}
            onCheck={toggleDetails}
            checked={showDetails}
          />
        )}

        <Row>
          <Col size={showDetails ? 6 : 12}>
            {isError && t("hybrisAjaxError")}

            {(isLoading || isCartLoading) && <CircleSpinner />}
            <Stack gap={2}>
              {!isLoading && (
                <>
                  {priceInfo && (
                    <PriceInput
                      label={
                        <SimplePriceText
                          variant={
                            product.quantityVisible === false
                              ? PriceTextVariants.monthlySubsriptionStartWithBigLetter
                              : PriceTextVariants.offer
                          }
                          priceUnit={priceUnit}
                        />
                      }
                      data-test-id={"pdp-input-offer-price"}
                      value={offerPrice?.value}
                      onChange={updateOfferPrice}
                      onBlur={blurOfferPrice}
                      disabled={wsError}
                      error={isInvalid}
                      helperLabel={
                        isSpecial ? (
                          <Typography component="div" variant="labelLarge" color="text.primary">
                            {obtainSpecialPriceLabel(t)}
                          </Typography>
                        ) : undefined
                      }
                    />
                  )}
                </>
              )}

              <VariantSelect />

              {isPriceRefVisible && (
                <PriceReferenceParam
                  data-test-id={"pdp-price-reference"}
                  priceReference={priceReference}
                  product={product}
                  externalLabel={true}
                  editable={canSeePriceRefColumn(selection)}
                  disabled={variantLoading}
                  onChange={(value) => formProps.setValue("priceReference", value ?? "")}
                />
              )}

              {quantityVisible && (
                <>
                  <QuantityInput
                    min={minQtyValue}
                    max={MAX_QUANTITY}
                    data-test-id={"pdp-input-quantity"}
                    value={quantity}
                    variant={"compact"}
                    disabled={!qtyInputEnabled || variantLoading}
                    step={packSize}
                    onValueChange={updateQuantity}
                    error={qtyIsError}
                    helperLabel={qtyHelperLabel}
                  />
                  <InfoOrderHandlingUnitQuantity
                    preferredPackQuantity={selectedPackage?.preferredPackQuantity ?? null}
                    packSize={selectedPackage?.packSize ?? 1}
                    quantity={quantity}
                  />
                </>
              )}
            </Stack>
          </Col>

          {showDetails && !wsError && (
            <Col size={6}>
              <div className={"scha__pdp-salesman__target-price-box"}>
                <PriceInput
                  label={<SimplePriceText variant={PriceTextVariants.targetPrice} priceUnit={priceUnit} />}
                  value={targetPrice?.value}
                  onChange={updateTargetPrice}
                />
                {canSeeListPrice({ product: productDescriptor, isSpecialPrice: isSpecial }) && (
                  <ListPrice price={priceInfo?.listPrice} classes={{ root: "mt-30" }} unit={priceUnit} />
                )}
              </div>
            </Col>
          )}
        </Row>

        {product.isOptime && <OptimeSolutionsMessage />}
        {showDetails && !isProductPMA(productDescriptor) && (
          <SalesmanPriceTable product={productDescriptor} selectedPackageCode={packCode} />
        )}

        {hasPreferredProducts && (
          <Link href={"#" + PREFERRED_PRODUCTS_ID}>
            {t("pdp.preferredProducts.title", { code: productFamilyName })}
          </Link>
        )}

        {isProductPMA(productDescriptor) && <InfoProductMessagePMA />}

        {!webServicesDisabled && !isLoading && userIsSupposedToSeePrice && productDescriptor.isError && (
          <Alert severity="error">{t("addProduct.wsError")}</Alert>
        )}

        <Stack gap={1}>{buttons}</Stack>
      </Stack>
    </Box>
  );

  return (
    <Grid container columns={12} spacing={3}>
      <Grid size={3}>{col1}</Grid>
      <Grid size={9}>
        <Stack gap={2}>
          {displayMMDAssistant && (
            <MMDAssistant
              marketDesignation={product.marketDesignation}
              initialData={{
                quantity: quantity,
                priceReference: priceReference,
                ...mmdCmnData,
              }}
            />
          )}

          <Grid container columns={9} component={Card}>
            <Grid size={5}>{col2}</Grid>
            <Grid size={4}>{col3}</Grid>
          </Grid>
        </Stack>
      </Grid>
    </Grid>
  );
};

const ListPrice = (props: Omit<ProductOtherPriceProps, "variant">) => {
  return (
    <Price
      variant={PriceType.ProductListPrice}
      {...props}
      sx={{
        margin: 0,
        ...getFontStyle(FontSize.BodyMedium),
        // Label order -1
        '& [data-test-id="unit"]': {
          order: -1,
        },
        // Price margin top
        '& [data-test-id="value"]': {
          marginTop: "5px",
        },
        ...props.sx,
      }}
    />
  );
};

// Copied from ExtendedPdpSummary
const Box = styled("div")({
  padding: "16px",
  height: "100%",
  position: "relative",
});
